import React, { useState, useRef, useEffect } from 'react';
import { FaCamera, FaTimes, FaCheck } from 'react-icons/fa';
import '../css/CameraCapture.css';

const CameraCapture = ({ onPhotoCapture, onClose, onRetake }) => {
  const [stream, setStream] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    startCamera();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Kamera nem támogatott ezen a böngészőben.');
      }
      
      let mediaStream;
      
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
      } catch (err) {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { min: 640, ideal: 1280, max: 1920 },
            height: { min: 480, ideal: 720, max: 1080 }
          }
        });
      }
      
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
        
        setTimeout(() => {
          if (videoRef.current && videoRef.current.paused) {
            videoRef.current.play();
          }
        }, 100);
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Kamera hozzáférés megtagadva. Kérjük, engedélyezze a kamera használatát.');
      } else if (err.name === 'NotFoundError') {
        setError('Nem található kamera az eszközön. Kérjük, ellenőrizze a webkamera csatlakozását.');
      } else if (err.name === 'NotReadableError') {
        setError('A kamera más alkalmazás által használatban van. Kérjük, zárja be a többi alkalmazást.');
      } else if (err.name === 'OverconstrainedError') {
        setError('A kamera nem támogatja a kért felbontást. Kérjük, próbálja újra.');
      } else {
        setError(`Kamera hiba: ${err.message || 'Ismeretlen hiba történt'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const photoUrl = URL.createObjectURL(blob);
        setPhoto({
          blob: blob,
          url: photoUrl
        });
      }
    }, 'image/jpeg', 0.8);
  };

  const retakePhoto = async () => {
    setPhoto(null);
    if (photo && photo.url) {
      URL.revokeObjectURL(photo.url);
    }
    
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    
    if (onRetake) {
      onRetake();
    } else {
      onClose();
    }
  };

  const confirmPhoto = () => {
    if (photo) {
      onPhotoCapture(photo.blob);
      if (photo.url) {
        URL.revokeObjectURL(photo.url);
      }
    }
  };

  const handleClose = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    if (photo && photo.url) {
      URL.revokeObjectURL(photo.url);
    }
    onClose();
  };

  if (isLoading) {
    return (
      <div className="camera-overlay">
        <div className="camera-modal">
          <div className="camera-loading">
            <div className="loading-spinner"></div>
            <p>Kamera indítása...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="camera-overlay">
        <div className="camera-modal">
          <div className="camera-error">
            <h3>Kamera hiba</h3>
            <p>{error}</p>
            <div className="camera-actions">
              <button onClick={startCamera} className="btn-retry">
                Újra próbálkozás
              </button>
              <button onClick={handleClose} className="btn-close">
                Bezárás
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="camera-overlay">
      <div className="camera-modal">
        <div className="camera-header">
        <div className="camera-header-close">
          <button onClick={handleClose} className="btn-close-header">
            <FaTimes />
          </button>
        </div>
          <h3>Fénykép készítése</h3>
          <p className='camera-header-text'>Kérjük készíts jól látható és éles képet a bejelenteni kívánt eseményről</p>
      
        </div>
      
        
        
        <div className="camera-content">
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          {!photo ? (
            <>
              <div className="video-container">
                    <video
                   ref={videoRef}
                   autoPlay
                   playsInline
                   muted
                   className="camera-video"
                   style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                 />
              </div>
                             <div className="camera-controls">
                 <button onClick={takePhoto} className="btn-capture">
                   <FaCamera />
                 </button>
               </div>
               <p className='camera-header-text'>Kattints a kamerára a fénykép készítéséhez</p>
            </>
          ) : (
            <>
              <div className="photo-preview-container">
                <img src={photo.url} alt="Captured photo" className="photo-preview" />
              </div>
              <div className="camera-controls">
                <button onClick={retakePhoto} className="btn-retake">
                  Újrafelvétel
                </button>
                <button onClick={confirmPhoto} className="btn-confirm">
                  <FaCheck />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CameraCapture; 